import { marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js/lib/core';
// Import common languages explicitly
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import java from 'highlight.js/lib/languages/java';
import sql from 'highlight.js/lib/languages/sql';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import xml from 'highlight.js/lib/languages/xml';
import markedKatex from 'marked-katex-extension';
import DOMPurify from 'dompurify';

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('c', c);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('c++', cpp);
hljs.registerLanguage('java', java);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('json', json);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);

/**
 * Configure marked with custom options for rendering quiz content
 */
let isConfigured = false;

export function configureMarked() {
  if (isConfigured) return; // Only configure once
  
  marked.setOptions({
    breaks: true,
    gfm: true,
  });

  // Syntax highlighting via highlight.js
  marked.use(
    markedHighlight({
      langPrefix: 'hljs language-',
      highlight(code, lang) {
        try {
          if (lang && hljs.getLanguage(lang)) {
            return hljs.highlight(code, { language: lang }).value;
          }
        } catch (e) {
          console.warn('Highlight.js error for language:', lang, e);
        }
        // Fallback to auto-detect or plaintext
        try {
          return hljs.highlightAuto(code).value;
        } catch (e) {
          console.warn('Highlight.js auto-detect error:', e);
          return escapeHtml(code); // Escape raw code to prevent HTML injection
        }
      },
    })
  );

  // Math formulas via KaTeX
  marked.use(
    markedKatex({
      // The extension handles both $...$ and $$...$$ by default
    })
  );
  
  isConfigured = true;
}

/**
 * Render markdown to HTML
 * Supports code blocks and inline math formulas
 * 
 * @param markdown - The markdown text to render
 * @returns HTML string
 */
export function renderMarkdown(markdown: string): string {
  configureMarked();
  const rendered = marked.parse(markdown) as string;
  return DOMPurify.sanitize(rendered, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'iframe', 'object', 'embed', 'form', 'input', 'button']
  });
}

/**
 * Escape HTML to prevent XSS attacks
 */
export function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}
